// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { IfFeature } from '@tectonics/prs/feature';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { InstalmentPlanText } from './mobile-installments-number';

import {
  type AllowedLocalTaxCodeSubstrings,
  getLocalTaxCodeFromString,
  amountToCurrencyString,
  fxToCurrencyString,
  taxRateToPercentageString,
} from '@tectonics/prs/money';

import {
  useCurrentReceipt,
  getLocalTax,
  getLocalTaxFromCodeSubstring,
  getFX,
} from '@tectonics/prs/receipts';

import { getInstalmentsPlan, getReceiptAmountExt } from '@tectonics/prs/receipts/receipt';
import { getMoneyFormat } from '../../commons/money-format';
import { TotalValue } from '../../molecules/total-value';
import { DisplayChileanRoundingDisclaimer } from '../side-display/display-chilean-rounding-disclaimer';
import { translate } from '@tectonics/translate';

export function InfoBox(): Node {
  const allFeatures = useAllFeatures();

  const receipt = useCurrentReceipt();
  const instalmentsPlan = getInstalmentsPlan(receipt);

  return (
    <InfoBoxWrapper data-testid="mobile_display_value">
      <DisplayValue>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_original_amount">
          <DisplayAmount />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_fx_rate">
          <DisplayFx />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_brazilian_tax_iof">
          <DisplayTax code="iof" />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_iva">
          <DisplayTax code="iva" percentage="21" />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_ig_ibp">
          <DisplayTax code="ig_ibp" percentage="35" />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_iibb">
          <DisplayTaxFromCodeSubstring codeSubstring="AR_IIBB_" percentage="2" />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_paraguayan_tax_iva">
          <DisplayTax code="py_iva" percentage="10" />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_costa_rican_tax_iva">
          <DisplayTax code="cr_iva" percentage="13" />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_el_salvadorian_tax_itbms">
          <DisplayTax code="sv_itbms" percentage="13" />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_indian_tax_gst">
          <DisplayTax code="inr_gst" percentage="18" />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_chilean_rounding_disclaimer">
          <DisplayChileanRoundingDisclaimer />
        </IfFeature>

        {instalmentsPlan && (
          <>
            <Separator />
            <IfFeature allFeatures={allFeatures} wanted="display_selected_instalment">
              <DisplayItem>
                <InfoItem><InstalmentsLabelText /></InfoItem>
                <InfoItem>
                  <InstalmentPlanText />
                </InfoItem>
              </DisplayItem>
            </IfFeature>
          </>
        )}

      </DisplayValue>

      <Separator />

      <DisplayValue data-testid="display_total_value">
        <DisplayItem>
          <Label>{translate('volcanes.total')}</Label>
          <Value>
            <TotalValue />
          </Value>
        </DisplayItem>
      </DisplayValue>

    </InfoBoxWrapper>
  );
}

function DisplayAmount(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
      return (
        <DisplayItem>
          <InfoItem>Amount</InfoItem>
          <InfoItem>
            {formatAmount(getReceiptAmountExt(receipt))}
          </InfoItem>
        </DisplayItem>
      );

    default:
      return (
        <DisplayItem>
          <InfoItem>{translate('volcanes.amount')}</InfoItem>
          <InfoItem>
            {formatAmount(getReceiptAmountExt(receipt))}
          </InfoItem>
        </DisplayItem>
      );
  }
}

function DisplayFx(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatFx = fxToCurrencyString(decimalFormat);

  return (
    <DisplayItem>
      <InfoItem>
        <FxMessage />
      </InfoItem>
      <InfoItem>
        {formatFx(getFX(receipt))}
      </InfoItem>
    </DisplayItem>
  );
}

function FxMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return <p>Taxa de Câmbio</p>;

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'cr':
    case 'py':
    case 'pa':
    case 'gt':
    case 'sv':
    case 'do':
      return <p>Tipo de Cambio</p>;

    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'eg':
    case 'gh':
    case 'jm':
      return <p>Exchange Rate</p>;

    default:
      throw new Error(`No message found for paying-by message in country ${country}`);
  }
}

type DisplayTaxProps = {|
  code: string,
  percentage?: string,
|};

function DisplayTax({ code, percentage }: DisplayTaxProps): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);
  const formatTax = taxRateToPercentageString(decimalFormat);
  const tax = getLocalTax(receipt, getLocalTaxCodeFromString(code));

  if (!tax) return null;

  const tax_percentage = formatTax(tax.rate);
  const percentage_value = percentage || tax_percentage;

  return (
    <DisplayItem>
      <InfoItem>{getTaxCodeLabel(code.toUpperCase())} ({percentage_value}%)</InfoItem>
      <InfoItem>
        {formatAmount(tax.amount)}
      </InfoItem>
    </DisplayItem>
  );
}

function getTaxCodeLabel(code: string): string {
  switch (code){
    case 'IG_IBP':
      return 'Ganancias';
    case 'INR_GST':
      return 'GST';
    default:
      return code;
  }
}


type DisplayTaxFromCodeSubstringProps = {|
  codeSubstring: AllowedLocalTaxCodeSubstrings,
  percentage: string,
|};

function DisplayTaxFromCodeSubstring({ codeSubstring, percentage }: DisplayTaxFromCodeSubstringProps): Node {
  const incomingPayment = useIncomingPayment();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);
  const receipt = useCurrentReceipt();
  const tax = getLocalTaxFromCodeSubstring(receipt, codeSubstring);

  if (!tax) return null;

  return (
    <DisplayItem>
      <InfoItem>{getTaxCodeLabel(tax.code.toUpperCase())} ({percentage}%)</InfoItem>
      <InfoItem>
        {formatAmount(tax.amount)}
      </InfoItem>
    </DisplayItem>
  );
}

function InstalmentsLabelText(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  switch (country) {
    case 'br':
      return 'Parcelamento';
    case 'mx':
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'pe':
    case 'uy':
    case 'py':
    case 'pa':
    case 'cr':
    case 'sv':
    case 'gt':
    case 'do':
      return 'Cuotas';
    case 'za':
    case 'ke':
    case 'ng':
    case 'eg':
    case 'jm':
      return 'Installments';
    default:
      throw new Error(`InstalmentsLabelText - country not supported ${country}`);
  }
}

const InfoBoxWrapper = styled.div`
  background: ${props => props.theme.colors.gray(97)};
  padding: 0 12px;
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 20px;
  height: 36px;
  font-family: ${props => props.theme.fontFamily.display};
`;

const Value = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 24px;
  font-family: ${props => props.theme.fontFamily.display};
  height: 36px;
`;

const DisplayValue = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 12px;
`;

const DisplayItem = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 4px 0;
  font-size: 14px;
  font-weight: 500;
  font-family: ${props => props.theme.fontFamily.sansSerif};
`;

const Separator = styled.div`
  border: 1px solid ${props => props.theme.colors.gray(95)};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 14px;
`;
